import styled from '@emotion/styled';
import { Badge, Stack, Text } from '@mantine/core';
import { LogType, type usePluginLogs } from '@morph-mapper/plugins';
import { PluginScrollBox } from './plugin-progress';

/*
 * The plugin logs, shared by the setup's Loading Plugins step and the
 * header's logs dropdown in the editor.
 */

export type PluginLog = ReturnType<typeof usePluginLogs>[number];

const LogItem = styled.div(({ theme }) => ({
  padding: '0.5rem',
  cursor: 'pointer',
  borderRadius: theme.radius.sm,
  transition: 'background-color 0.2s',
  backgroundColor: 'white',
  borderBottom: `1px solid ${theme.colors.gray[3]}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.colors.blue[1],
  },
}));

const getLogColor = (type: LogType) => {
  if (type === LogType.Error) return 'red';
  if (type === LogType.Warning) return 'orange';
  if (type === LogType.Info) return 'cyan';
  if (type === LogType.Success) return 'green';
};

const LogRow = ({ log }: { log: PluginLog }) => (
  <LogItem>
    <Badge
      color={getLogColor(log.type)}
      radius="xs"
      variant="outline"
      sx={{ opacity: 1 }}
      miw={80}
      mr="md"
    >
      {log.type}
    </Badge>
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Text>{log.message}</Text>
    </div>
    <Text size="sm" color="dimmed" style={{ marginLeft: '1rem', whiteSpace: 'nowrap' }}>
      {log.plugin}
    </Text>
  </LogItem>
);

/** The logs in the order given, in the shared scroll box. */
export const PluginLogList = ({ logs }: { logs: PluginLog[] }) => (
  <PluginScrollBox>
    <Stack spacing="xs">
      {logs.map((log, index) => (
        <LogRow key={`${log.time}-${index}-${log.plugin}`} log={log} />
      ))}
    </Stack>
  </PluginScrollBox>
);
